// MessageForm.jsx - Version nettoyée avec hook useHybridMessaging

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Archive, Search, Send, ChevronRight, X, Check, 
  Users, MessageCircle, Paperclip, Image, File, Plus,
  Circle, CheckCircle2, Loader2, AlertCircle, MoreVertical, Trash2, ArchiveRestore, Info, Eye, Download, CheckCheck
} from 'lucide-react';
import { api } from '../services/api.js'; 
import RecipientSelector from './RecipientSelector'; 
import ConfirmDialog from '../components/ConfirmDialog';
import { Toast } from "../components/ui/Toast";

// 🆕 IMPORT DU NOUVEAU HOOK
import useHybridMessaging from '../hooks/useHybridMessaging';

// ====================================
// COMPOSANT NOUVEAU MESSAGE
// ====================================
const NewMessageForm = ({ 
  selectedRecipients, 
  setSelectedRecipients,
  messageSubject, 
  setMessageSubject,
  newMessageContent, 
  setNewMessageContent,
  attachments,
  handleFileAttachment,
  removeAttachment,
  sending,
  sendResults,
  resetForm,
  handleSendUnified,
  error
}) => (
  <div className="space-y-4 bg-white rounded-lg p-4">
    {/* Résultats d'envoi */}
    {sendResults && (
      <div className={`p-4 rounded-lg border-l-4 ${
        sendResults.success 
          ? 'bg-green-50 border-green-400' 
          : 'bg-red-50 border-red-400'
      }`}>
        <div className="flex items-center gap-2">
          {sendResults.success ? (
            <Check size={20} className="text-green-600" />
          ) : (
            <AlertCircle size={20} className="text-red-600" />
          )}
          <p className={`font-medium ${
            sendResults.success ? 'text-green-800' : 'text-red-800'
          }`}>
            {sendResults.message}
          </p>
        </div>
        
        {(sendResults.totalSent > 0 || sendResults.totalErrors > 0) && (
          <div className="mt-2 text-sm space-y-1">
            {sendResults.totalSent > 0 && (
              <p className="text-green-700">
                ✅ {sendResults.totalSent} message{sendResults.totalSent > 1 ? 's' : ''} envoyé{sendResults.totalSent > 1 ? 's' : ''}
              </p>
            )}
            {sendResults.totalErrors > 0 && (
              <p className="text-red-700">
                ❌ {sendResults.totalErrors} erreur{sendResults.totalErrors > 1 ? 's' : ''}
              </p>
            )}
          </div>
        )}
        
        {sendResults.success && (
          <p className="text-sm text-green-600 mt-2">
            Redirection vers les conversations dans quelques secondes...
          </p>
        )}
      </div>
    )}

    {/* Sélecteur de destinataires */}
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Destinataires *
      </label>
      <RecipientSelector
        selectedRecipients={selectedRecipients}
        onSelectionChange={setSelectedRecipients}
        className="w-full"
      />
      <p className="text-xs text-gray-500">
        Vous pouvez sélectionner plusieurs groupes et employés. 
        Les groupes créeront des conversations de groupe, 
        les employés des conversations individuelles.
      </p>
    </div>

    {/* Sujet optionnel */}
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Sujet
      </label>
      <input
        type="text"
        value={messageSubject}
        onChange={(e) => setMessageSubject(e.target.value)}
        className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-[#319190] focus:border-[#319190]"
        maxLength={100}
      />
    </div>
    
    {/* Contenu du message */}
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        Message *
      </label>
      <textarea
        value={newMessageContent}
        onChange={(e) => {
          if (e.target.value.length <= 5000) {
            setNewMessageContent(e.target.value);
          }
        }} 
        className="w-full p-3 border rounded-lg h-32 resize-none focus:ring-2 focus:ring-[#319190] focus:border-[#319190]"
        maxLength={5000}
        placeholder="Tapez votre message..."
      />
      <div className="text-xs text-gray-500 text-right">
        {newMessageContent.length}/5000 caractères
      </div>
      
      {newMessageContent.length >= 4900 && (
        <div className="text-xs text-orange-600">
          ⚠️ Limite de caractères bientôt atteinte ({5000 - newMessageContent.length} restants)
        </div>
      )}
    </div>

    {/* Pièces jointes */}
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
        <Paperclip size={16} />
        Pièces jointes
        <input
          type="file"
          multiple
          className="hidden"
          onChange={handleFileAttachment}
          accept="image/*,.pdf,.doc,.docx,.txt,.xlsx,.zip"
        />
      </label>

      {attachments.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            Fichiers joints ({attachments.length}) :
          </p>
          <div className="space-y-2 max-h-32 overflow-y-auto">
            {attachments.map(attachment => (
              <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center gap-3">
                  {attachment.type === 'image' ? (
                    <Image size={20} className="text-blue-500" />
                  ) : (
                    <File size={20} className="text-gray-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium">{attachment.name}</p>
                    <p className="text-xs text-gray-500">
                      {(attachment.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                  title="Supprimer"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>

    {/* Aperçu de la sélection */}
    {selectedRecipients.length > 0 && (
      <div className="bg-gray-50 rounded-lg p-3">
        <p className="text-sm font-medium text-gray-700 mb-2">
          Aperçu de l'envoi ({selectedRecipients.length} destinataire{selectedRecipients.length > 1 ? 's' : ''}) :
        </p>
        <div className="space-y-1 text-sm">
          {selectedRecipients.filter(r => r.type === 'group').length > 0 && (
            <p className="text-blue-700">
              📢 {selectedRecipients.filter(r => r.type === 'group').length} groupe{selectedRecipients.filter(r => r.type === 'group').length > 1 ? 's' : ''} de diffusion
            </p>
          )}
          {selectedRecipients.filter(r => r.type === 'individual').length > 0 && (
            <p className="text-green-700">
              👤 {selectedRecipients.filter(r => r.type === 'individual').length} conversation{selectedRecipients.filter(r => r.type === 'individual').length > 1 ? 's' : ''} individuelle{selectedRecipients.filter(r => r.type === 'individual').length > 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    )}

    {/* Actions */}
    <div className="flex gap-3 pt-4 border-t">
      <button
        onClick={resetForm}
        disabled={sending}
        className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        Annuler
      </button>
      
      <button
        onClick={handleSendUnified}
        disabled={selectedRecipients.length === 0 || (!newMessageContent.trim() && attachments.length === 0) || sending}
        className="flex-1 bg-[#319190] text-white px-4 py-2.5 rounded-lg hover:bg-[#319190]/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
      >
        {sending ? (
          <>
            <Loader2 size={16} className="animate-spin" />
            Envoi en cours...
          </>
        ) : (
          <>
            <Send size={16} />
            Envoyer ({selectedRecipients.length})
          </>
        )}
      </button>
    </div>
  </div>
);

// ====================================
// COMPOSANT CONVERSATION VIEW
// ====================================
const ConversationView = ({ 
  selectedConversation,
  setSelectedConversation,
  leaveConversation,
  isUserOnline,
  isConnected,
  loading,
  messages,
  chatMessageContent,
  setChatMessageContent,
  attachments,
  removeAttachment,
  fileInputRef,
  handleFileAttachment,
  sending,
  sendMessage,
  typingUsers,
  messagesEndRef,
  renderAvatar,
  messageReadStatus,  
  markMessageAsReadSocket 
}) => (
  <div className="flex flex-col h-full relative">
    {/* Header conversation - FIXE EN HAUT */}
    <div className="absolute top-0 left-0 right-0 h-16 flex items-center gap-3 p-4 border-b bg-white z-10">
      <button
        onClick={() => {
          if (selectedConversation) {
            leaveConversation(selectedConversation.id);
          }
          setSelectedConversation(null);
        }}
        className="p-1 hover:bg-gray-100 rounded-full"
      >
        <ChevronRight size={20} className="rotate-180" />
      </button>
      
      <div className="flex items-center gap-3 flex-1">
        {selectedConversation.type === 'individual' ? (
          <>
            <div className="relative">
              {(() => {
                console.log('🖼️ [ConversationView] Avatar data:', {
                  selectedConversation: selectedConversation,
                  other_participant: selectedConversation.other_participant,
                  individual_photo: selectedConversation.individual_photo
                });
                
                let employeeData = null;
                
                if (selectedConversation.other_participant) {
                  employeeData = selectedConversation.other_participant;
                  console.log('✅ [ConversationView] Source: other_participant');
                }
                
                else if (selectedConversation.individual_photo) {
                  employeeData = {
                    id: selectedConversation.other_participant_id,
                    photo_url: selectedConversation.individual_photo,
                    prenom: selectedConversation.display_name?.split(' ')[0] || 'U',
                    nom: selectedConversation.display_name?.split(' ')[1] || 'ser'
                  };
                  console.log('✅ [ConversationView] Source: individual_photo');
                }
                
                if (employeeData) {
                  return (
                    <div className="relative">
                      <div className="w-10 h-10">
                        {renderAvatar(employeeData, 40)}
                      </div>
                    </div>
                  );
                }
                
                return (
                  <div className="w-10 h-10 bg-[#319190] rounded-full flex items-center justify-center text-white font-medium">
                    {selectedConversation.display_name?.substring(0, 2).toUpperCase() || '??'}
                  </div>
                );
              })()}
              
              {isUserOnline(selectedConversation.other_participant_id) && (
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>

            <div>
              <h3 className="font-medium">{selectedConversation.display_name}</h3>
              <p className="text-sm text-gray-500">
                {isUserOnline(selectedConversation.other_participant_id) ? 'En ligne' : 'Hors ligne'}
              </p>
            </div>
          </>
        ) : (
          <>
            <div className="w-10 h-10 bg-[#319190] rounded-full flex items-center justify-center text-white">
              <Users size={20} />
            </div>
            <div>
              <h3 className="font-medium">{selectedConversation.name}</h3>
              <p className="text-sm text-gray-500">
                {selectedConversation.participant_count} membres
              </p>
            </div>
          </>
        )}
      </div>

      {/* Indicateur de connexion Socket.io */}
      <div className="flex items-center gap-2">
        {isConnected ? (
          <Circle size={8} className="text-green-500 fill-current" title="Temps réel actif" />
        ) : (
          <Circle size={8} className="text-red-500 fill-current" title="Hors ligne" />
        )}
      </div>
    </div>

    {/* Zone messages - SCROLLABLE ENTRE HEADER ET FOOTER */}
    <div className="absolute top-16 bottom-40 left-0 right-0 overflow-y-auto p-4">
      {loading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 size={24} className="animate-spin text-[#319190]" />
        </div>
      ) : (
        <>
          {selectedConversation.isGroupWithoutConversation && messages.length === 0 && (
            <div className="text-center text-gray-500 py-8">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p className="font-medium mb-2">💬 {selectedConversation.group_info.name}</p>
              <p className="text-sm">Aucun message pour l'instant</p>
              <p className="text-sm text-gray-400 mt-2">
                Écrivez le premier message pour démarrer la conversation du groupe !
              </p>
            </div>
          )}
          
          <div className="space-y-4">
            {messages.map(message => (
              <div
                key={message.id}
                className={`flex ${message.isOwn || message.sender_id === JSON.parse(localStorage.getItem('user') || '{}').id ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  message.isOwn || message.sender_id === JSON.parse(localStorage.getItem('user') || '{}').id
                    ? 'bg-[#319190] text-white'
                    : 'bg-[#246990] text-white'
                } ${message.is_deleted ? 'opacity-50 italic' : ''}`}>
                  {(!message.isOwn && message.sender_id !== JSON.parse(localStorage.getItem('user') || '{}').id) && selectedConversation.type === 'group' && (
                    <p className="text-xs font-medium mb-1 opacity-70">
                      {message.sender_prenom} {message.sender_nom}
                    </p>
                  )}
                  
                  <p className="text-sm">{message.content}</p>
                  
                  {/* Pièces jointes */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message.attachments.map(attachment => {
                        const isImage = attachment.is_image || attachment.mimetype?.startsWith('image/') || attachment.file_type?.startsWith('image/');
                        
                        const viewUrl = `https://www.klasys.com/api/messagerie/attachments/${attachment.id}/view`;
                        const downloadUrl = `https://www.klasys.com/api/messagerie/attachments/${attachment.id}/download`;
                        
                        console.log('🔗 [MessageForm] URLs générées:', { 
                          attachment_id: attachment.id,
                          original_filename: attachment.original_filename,
                          viewUrl, 
                          downloadUrl 
                        });
                                      
                        return (
                          <div key={attachment.id} className="bg-white/10 rounded-lg p-2 max-w-64">
                            {isImage ? (
                              <div className="space-y-2">
                                <div className="relative group">
                                  <img 
                                    src={viewUrl}
                                    alt={attachment.original_filename}
                                    className="max-w-full max-h-48 rounded object-cover cursor-pointer hover:opacity-90 transition-opacity"
                                    onClick={() => window.open(viewUrl, '_blank')}
                                    onError={(e) => {
                                      console.error('❌ Erreur chargement image:', viewUrl);
                                      e.target.style.display = 'none';
                                      e.target.nextSibling.style.display = 'block';
                                    }}
                                  />
                                  <div className="hidden bg-gray-200 p-4 rounded text-center text-gray-600">
                                    <Image size={24} className="mx-auto mb-2" />
                                    <p className="text-sm">Image non disponible</p>
                                  </div>
                                  
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center gap-2">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        window.open(viewUrl, '_blank');
                                      }}
                                      className="bg-white/90 hover:bg-white text-gray-800 px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 shadow-sm transition-all hover:scale-105"
                                      title="Visualiser l'image"
                                    >
                                      <Eye size={14} />
                                      <span>Voir</span>
                                    </button>
                                    <a 
                                      href={downloadUrl}
                                      download={attachment.original_filename}
                                      className="bg-white/90 hover:bg-white text-gray-800 px-3 py-2 rounded-md text-xs font-medium flex items-center gap-1.5 shadow-sm transition-all hover:scale-105 no-underline"
                                      onClick={(e) => e.stopPropagation()}
                                      title="Télécharger l'image"
                                    >
                                      <Download size={14} />
                                      <span>Télécharger</span>
                                    </a>
                                  </div>
                                </div>
                                
                                <div className="flex items-center justify-between text-xs">
                                  <span className="truncate text-white/80">{attachment.original_filename}</span>
                                  {attachment.file_size && (
                                    <span className="text-white/60 ml-2">
                                      {(attachment.file_size / 1024 / 1024).toFixed(1)} MB
                                    </span>
                                  )}
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                  <div className="w-8 h-8 bg-white/20 rounded flex items-center justify-center flex-shrink-0">
                                    <File size={16} className="text-white" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium truncate text-white">
                                      {attachment.original_filename}
                                    </p>
                                    <p className="text-xs text-white/70">
                                      {attachment.file_size ? `${(attachment.file_size / 1024 / 1024).toFixed(1)} MB` : 'Fichier'}
                                    </p>
                                  </div>
                                </div>
                                
                                <div className="flex gap-1 flex-shrink-0">
                                  <button
                                    onClick={() => window.open(viewUrl, '_blank')}
                                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-md transition-all hover:scale-105 flex items-center justify-center group"
                                    title="Ouvrir le fichier"
                                  >
                                    <Eye size={14} className="group-hover:scale-110 transition-transform" />
                                  </button>
                                  <a 
                                    href={downloadUrl}
                                    download={attachment.original_filename}
                                    className="bg-white/20 hover:bg-white/30 text-white p-2 rounded-md transition-all hover:scale-105 flex items-center justify-center group no-underline"
                                    title="Télécharger le fichier"
                                  >
                                    <Download size={14} className="group-hover:scale-110 transition-transform" />
                                  </a>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-xs ${
                      message.isOwn || message.sender_id === JSON.parse(localStorage.getItem('user') || '{}').id 
                        ? 'text-white/70' 
                        : 'text-white/70'
                    }`}>
                      {(() => {
                        if (!message.created_at) return '';
                        
                        try {
                          const now = new Date();
                          const date = new Date(message.created_at);
                          if (isNaN(date.getTime())) return '';
                          
                          const diffMs = now - date;
                          const diffMinutes = Math.floor(diffMs / (1000 * 60));
                          
                          const isToday = now.toDateString() === date.toDateString();
                          
                          const yesterday = new Date(now);
                          yesterday.setDate(yesterday.getDate() - 1);
                          const isYesterday = yesterday.toDateString() === date.toDateString();
                          
                          if (diffMinutes < 1) {
                            return "À l'instant";
                          }
                          
                          if (isToday) {
                            return date.toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            });
                          }
                          
                          if (isYesterday) {
                            return `Hier ${date.toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`;
                          }
                          
                          if (diffMs < 7 * 24 * 60 * 60 * 1000) {
                            const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                            const dayName = dayNames[date.getDay()];
                            return `${dayName} ${date.toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`;
                          }
                          
                          if (now.getFullYear() === date.getFullYear()) {
                            return `${date.toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit'
                            })} ${date.toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}`;
                          }
                          
                          return `${date.toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })} ${date.toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}`;
                          
                        } catch (error) {
                          return '';
                        }
                      })()}
                    </p>
                    
                    {/* Statut de lecture pour les messages envoyés */}
                    {(message.isOwn || message.sender_id === JSON.parse(localStorage.getItem('user') || '{}').id) && (
                      <div className="ml-2 flex items-center">
                        {(() => {
                          const readStatus = messageReadStatus.get(message.id);
                          const isRead = readStatus?.isRead || message.read_count > 0;

                          console.log(`🔍 [CheckCheck] Message ${message.id}:`, {
                            readStatus: readStatus,
                            isRead: isRead,
                            read_count: message.read_count,
                            messageReadStatus_size: messageReadStatus.size,
                            message_sender_id: message.sender_id,
                            current_user_id: JSON.parse(localStorage.getItem('user') || '{}').id
                          });
                                               
                          return (
                            <div className="flex items-center gap-1" title={
                              isRead 
                                ? `Lu ${readStatus?.readAt ? new Date(readStatus.readAt).toLocaleString('fr-FR') : ''}`
                                : 'Non lu'
                            }>
                              {isRead ? (
                                <CheckCheck size={12} className="text-blue-400" />
                              ) : (
                                <Check size={12} className="text-white/50" />
                              )}
                              
                              {selectedConversation?.type === 'group' && message.read_count > 0 && (
                                <span className="text-xs bg-blue-200 text-blue-800 px-1 rounded-full ml-1">
                                  {message.read_count}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Indicateur de frappe */}
            {typingUsers.length > 0 && (
              <div className="flex justify-start">
                <div className="bg-gray-100 rounded-lg px-4 py-2">
                  <p className="text-sm text-gray-600">
                    {typingUsers.map(user => `${user.prenom}`).join(', ')} 
                    {typingUsers.length === 1 ? ' tape' : ' tapent'}...
                  </p>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </>
      )}
    </div>

    {/* Zone de saisie - FIXE EN BAS */}
    <div className="absolute bottom-0 left-0 right-0 bg-white border-t">
      {/* Pièces jointes */}
      {attachments.length > 0 && (
        <div className="p-3 border-b bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {attachments.map(attachment => (
              <div key={attachment.id} className="flex items-center gap-2 bg-white rounded-lg p-2 shadow-sm">
                {attachment.type === 'image' ? (
                  <Image size={16} className="text-blue-500" />
                ) : (
                  <File size={16} className="text-gray-500" />
                )}
                <span className="text-sm truncate max-w-32">{attachment.name}</span>
                <button
                  onClick={() => removeAttachment(attachment.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Champ de saisie avec limitation 5000 caractères */}
      <div className="p-4">
        <div className="flex items-end gap-2">
          <div className="flex-1 relative">
            <textarea
              value={chatMessageContent} 
              onChange={(e) => {
                if (e.target.value.length <= 5000) {
                  setChatMessageContent(e.target.value);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (!sending && (chatMessageContent.trim() || attachments.length > 0)) {
                    sendMessage();
                  }
                }
              }}
              className="w-full p-3 border rounded-lg resize-none focus:ring-2 focus:ring-[#319190] focus:border-[#319190] pr-16"
              rows="3"
              style={{ 
                minHeight: '80px', 
                maxHeight: '120px'
              }}
              placeholder="Tapez votre message..."
              autoComplete="off"
              spellCheck="false"
              disabled={sending}
              maxLength={5000}
            />
            
            {/* Compteur de caractères */}
            <div className="absolute bottom-2 right-2 text-xs text-gray-400 bg-white px-1 rounded">
              {chatMessageContent.length}/5000
            </div>
          </div>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileAttachment}
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-3 text-gray-400 hover:text-[#319190] cursor-pointer transition-colors border rounded-lg hover:bg-gray-50"
            type="button"
            title="Joindre un fichier"
          >
            <Paperclip size={20} />
          </button>
          
          <button
            onClick={() => {
              if (!sending && (chatMessageContent.trim() || attachments.length > 0)) {
                sendMessage();
              }
            }}
            disabled={(!chatMessageContent.trim() && attachments.length === 0) || sending || chatMessageContent.length > 5000}
            className="bg-[#319190] text-white p-3 rounded-lg hover:bg-[#319190]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            type="button"
          >
            {sending ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              <Send size={20} />
            )}
          </button>
        </div>
        
        {/* Alerte si limite atteinte */}
        {chatMessageContent.length >= 4900 && (
          <div className="mt-2 text-xs text-orange-600">
            ⚠️ Limite de caractères bientôt atteinte ({5000 - chatMessageContent.length} restants)
          </div>
        )}
      </div>
    </div>
  </div>
);

// ====================================
// COMPOSANT PRINCIPAL AVEC HOOK
// ====================================
const MessageForm = ({ isOpen, onClose, onUnreadCountChange }) => {
  // ✅ UTILISER LE NOUVEAU HOOK (Remplace 90% de la logique)

  const onUnreadCountChangeRef = useRef();
onUnreadCountChangeRef.current = onUnreadCountChange;

const stableOnUnreadCountChange = useCallback((count) => {
  if (onUnreadCountChangeRef.current) {
    onUnreadCountChangeRef.current(count);
  }
}, []);


  const {
    // États principaux
    conversations,
    setConversations,
    groups,
    setGroups,
    employees,
    setEmployees,
    messages,
    selectedConversation,
    setSelectedConversation,
    loading,
    setLoading,
    error,
    setError,

    // États temps réel
    isConnected,
    isReconnecting,
    typingUsers,
    onlineUsers,
    messageReadStatus,

    // États groupes et archivage
    groupConversations,
    setGroupConversations,
    groupMessages,
    setGroupMessages,
    archivedConversations,
    setArchivedConversations,

    // Références
    socketRef,
    messagesEndRef,
    typingTimeoutRef,

    // Actions principales
    fetchMessages,
    sendMessage: sendMessageFromHook,
    openConversation,
    joinConversation,
    leaveConversation,
    
    // Utilitaires
    scrollToBottom,
    isUserOnline,
    forceSync,

    // 🆕 Nouvelles fonctions hybrides
    fetchMessagesSafely,
    fetchConversationsSafely,
    lastSyncTimestamp,
    syncInProgress

 } = useHybridMessaging(stableOnUnreadCountChange);

  // ====================================
  // ÉTATS SPÉCIFIQUES AU COMPOSANT
  // ====================================
  const [currentView, setCurrentView] = useState('conversations');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [avatarCache, setAvatarCache] = useState(new Map());
  const [imageErrors, setImageErrors] = useState(new Set());
  const [forceRefresh, setForceRefresh] = useState(0);

  // États pour nouveau message
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [messageSubject, setMessageSubject] = useState('');
  const [sending, setSending] = useState(false); 
  const [sendResults, setSendResults] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // États pour recherche
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    messages: [],
    conversations: [],
    users: []
  });
  const [isSearching, setIsSearching] = useState(false);
  
  const [attachments, setAttachments] = useState([]);
  const [chatMessageContent, setChatMessageContent] = useState(''); 
  const [newMessageContent, setNewMessageContent] = useState('');  

  // Refs spécifiques
  const fileInputRef = useRef(null);

  // ====================================
  // 🆕 WRAPPER DU HOOK sendMessage
  // ====================================
  const sendMessage = useCallback(async () => {
    if (!chatMessageContent.trim() && attachments.length === 0) return;
    if (sending) return;

    try {
      setSending(true);
      
      // 🎯 UTILISER LA FONCTION DU HOOK
      await sendMessageFromHook(chatMessageContent, attachments);
      
      // Reset formulaire
      setChatMessageContent('');
      setAttachments([]);
      
    } catch (error) {
      console.error('❌ [MessageForm] Erreur sendMessage:', error);
      setError('Impossible d\'envoyer le message');
    } finally {
      setSending(false);
    }
  }, [chatMessageContent, attachments, sending, sendMessageFromHook]);

  // ====================================
  // FONCTIONS MÉTIER CONSERVÉES
  // ====================================

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      
      const [dashboardResult, myGroupsResult, groupConversationsResult] = await Promise.all([
        api.messagerie.getDashboard(),
        api.messagerie.getMyGroups(),
        api.messagerie.getMyGroupConversations()
      ]);
      
      if (!dashboardResult.success) {
        throw new Error(dashboardResult.error || 'Erreur lors du chargement du dashboard');
      }
      
      const data = dashboardResult.data;
      
      const individualConversationsOnly = (data.conversations || []).filter(conv => {
        if (conv.type === 'group') return false;
        if ((conv.name || conv.display_name || '').includes('💬')) return false;
        return true;
      });
      
      setConversations(individualConversationsOnly);

      let allGroups = [];
      
      if (myGroupsResult.success) {
        const myGroups = myGroupsResult.data || [];
        allGroups = [...myGroups];
      }
      
      if (groupConversationsResult.success) {
        const existingGroupConvs = groupConversationsResult.data || [];
        
        existingGroupConvs.forEach(conv => {
          const groupId = conv.group_id || conv.group_info?.id;
          if (groupId) {
            setGroupConversations(prev => new Map(prev.set(groupId, conv)));
            loadGroupMessages(conv.id, groupId);
          }
        });
        
        allGroups = allGroups.map(group => {
          const existingConv = existingGroupConvs.find(conv => 
            (conv.group_id || conv.group_info?.id) === group.id
          );
          
          if (existingConv) {
            return {
              ...existingConv,
              member_count: group.member_count,
              description: group.description
            };
          }
          
          return group;
        });
      }
      
      setGroups(allGroups);

    } catch (error) {
      console.error('❌ Erreur chargement dashboard:', error);
      setError('Impossible de charger les données');
    } finally {
      setLoading(false);
    }
  };

  const loadGroupMessages = async (conversationId, groupId) => {
    try {
      const result = await api.messagerie.getMessages(conversationId);
      
      if (result.success && result.data) {
        setGroupMessages(prev => new Map(prev.set(groupId, result.data)));
      }
    } catch (error) {
      console.error(`❌ Erreur chargement messages groupe ${groupId}:`, error);
    }
  };

  const fetchEmployees = async (searchTerm = '') => {
    try {
      const result = await api.messagerie.getEmployeesWithAccess(searchTerm);

      if (!result.success) {
        setEmployees([]);
        return;
      }

      const employeesData = result.data || [];
      setEmployees(employeesData);
      
    } catch (error) {
      console.error('❌ Erreur chargement employés:', error);
      setEmployees([]);
    }
  };

  const markMessageAsReadSocket = useCallback((message) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (message.sender_id === currentUser.id) return;

    const readStatus = messageReadStatus.get(message.id);
    if (readStatus?.isRead) return;

    if (socketRef.current && selectedConversation?.id) {
      socketRef.current.emit('message_read', {
        messageId: message.id,
        conversationId: selectedConversation.id
      });
    }
  }, [selectedConversation?.id, messageReadStatus, socketRef]);

  const openGroupConversation = async (group) => {
    console.log(`🔍 [openGroupConversation] Ouverture groupe:`, group);
    
    try {
      if (selectedConversation) {
        leaveConversation(selectedConversation.id);
      }
      
      const groupId = group.id;
      
      if (group.type === 'group' && group.id && !group.id.toString().startsWith('group_')) {
        console.log(`✅ [openGroupConversation] Conversation existante détectée - ID: ${group.id}`);
        
        setSelectedConversation(group);
        setCurrentView('groups'); 
        
        joinConversation(group.id);
        
        try {
          const result = await api.messagerie.getMessages(group.id);
          if (result.success && result.data) {
            // Note: setMessages est géré par le hook maintenant
            console.log(`✅ ${result.data.length} messages chargés pour conversation existante`);
          }
        } catch (msgError) {
          console.error('❌ Erreur chargement messages conversation existante:', msgError);
        }
        
        return;
      }
      
      let savedConversation = groupConversations.get(groupId);
      let savedMessages = groupMessages.get(groupId) || [];
      
      if (savedConversation) {
        console.log(`🔄 Restauration conversation sauvée pour groupe ${group.name}`);
        setSelectedConversation(savedConversation);
        setCurrentView('chat');
        
        if (savedConversation.id && !savedConversation.id.toString().startsWith('group_')) {
          joinConversation(savedConversation.id);
        }
        
        return;
      }
      
      console.log(`🆕 Première ouverture groupe ${group.name || group.display_name} - création conversation temporaire`);
      
      const groupConversation = {
        id: `group_${groupId}`,
        name: `💬 ${group.name || group.display_name}`,
        type: 'group',
        display_name: `💬 ${group.name || group.display_name}`,
        group_info: {
          id: groupId,
          name: group.name || group.display_name,
          description: group.description
        },
        participant_count: group.member_count || 0,
        isGroupWithoutConversation: true,
        last_message_content: "Aucun message pour l'instant"
      };
      
      setGroupConversations(prev => new Map(prev.set(groupId, groupConversation)));
      setGroupMessages(prev => new Map(prev.set(groupId, [])));
      
      setSelectedConversation(groupConversation);
      setCurrentView('chat');
      
    } catch (error) {
      console.error('❌ Erreur ouverture groupe:', error);
      setError('Impossible d\'ouvrir la conversation de groupe');
    }
  };

  // ====================================
  // FONCTIONS UTILITAIRES
  // ====================================

  const showToast = (message, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  };

  const closeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const resetForm = () => {
    setSelectedRecipients([]);
    setNewMessageContent('');
    setMessageSubject('');
    setAttachments([]);
    setSendResults(null);
  };

  const handleSendUnified = async () => {
    if (selectedRecipients.length === 0) {
      setError('Sélectionnez au moins un destinataire');
      return;
    }

    if (!newMessageContent.trim() && attachments.length === 0) {
      setError('Saisissez un message ou ajoutez une pièce jointe');
      return;
    }

    try {
      setSending(true);
      setError(null);

      const finalContent = messageSubject.trim() 
         ? `**${messageSubject.trim()}**\n\n${newMessageContent}`
        : newMessageContent;

      const result = await api.messagerie.sendUnifiedMessage(selectedRecipients, {
        content: finalContent,
        messageType: attachments.length > 0 ? 'file' : 'text',
        attachments: attachments.map(att => att.file)
      });

      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }

      const formattedResults = api.messagerie.formatSendResults(result.data);
      setSendResults(formattedResults);

      if (formattedResults.totalErrors === 0) {
        setTimeout(() => {
          resetForm();
          setCurrentView('conversations');
          fetchDashboard();
        }, 2000);
      }

    } catch (error) {
      console.error('❌ Erreur envoi unifié:', error);
      setError(error.message);
    } finally {
      setSending(false);
    }
  };

  const handleFileAttachment = (event) => {
    const files = Array.from(event.target.files);
    const newAttachments = files.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type.startsWith('image/') ? 'image' : 'file'
    }));
    setAttachments([...attachments, ...newAttachments]);
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(attachments.filter(att => att.id !== attachmentId));
  };

  const archiveConversation = async (conversationId) => {
    try {
      setIsArchiving(true);
      
      const result = await api.messagerie.archiveConversation(conversationId);
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors de l\'archivage');
      }
      
      const conversationToArchive = conversations.find(conv => conv.id === conversationId);
      if (conversationToArchive) {
        setArchivedConversations(prev => [
          { ...conversationToArchive, archived_at: new Date().toISOString() },
          ...prev
        ]);
        
        setConversations(prev => prev.filter(conv => conv.id !== conversationId));
        
        if (selectedConversation?.id === conversationId) {
          setSelectedConversation(null);
        }
      }
      
      showToast(`Votre conversation avec ${conversationToArchive.display_name || conversationToArchive.name} a été archivée`, 'success');
      await loadArchivedConversations();
      
    } catch (error) {
      console.error('❌ Erreur archivage:', error);
      setError('Impossible d\'archiver la conversation');
    } finally {
      setIsArchiving(false);
    }
  };

  const unarchiveConversation = async (conversationId) => {
    try {
      setIsArchiving(true);
      
      const result = await api.messagerie.unarchiveConversation(conversationId);
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur lors du désarchivage');
      }
      
      const conversationToUnarchive = archivedConversations.find(conv => conv.id === conversationId);
      if (conversationToUnarchive) {
        setArchivedConversations(prev => prev.filter(conv => conv.id !== conversationId));
        
        const { archived_at, ...cleanConversation } = conversationToUnarchive;
        setConversations(prev => [cleanConversation, ...prev]);
      }
      
      showToast(`Votre conversation avec ${conversationToUnarchive.display_name || conversationToUnarchive.name} a été restaurée`, 'success');
      await loadArchivedConversations();
      
    } catch (error) {
      console.error('❌ Erreur désarchivage:', error);
      setError('Impossible de restaurer la conversation');
    } finally {
      setIsArchiving(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    const conv = archivedConversations.find(c => c.id === conversationId);
    const conversationName = conv?.display_name || conv?.name || 'cette conversation';
    
    setConfirmDialog({
      title: 'Supprimer définitivement',
      description: `Êtes-vous sûr de vouloir supprimer définitivement votre conversation avec ${conversationName} ?\n\nCette action est irréversible et supprimera tous les messages.`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'error',
      onConfirm: async () => {
        try {
          setIsDeleting(true);
          
          const result = await api.messagerie.deleteConversation(conversationId);
          
          if (!result.success) {
            throw new Error(result.error || 'Erreur lors de la suppression');
          }
          
          setArchivedConversations(prev => prev.filter(conv => conv.id !== conversationId));
          
          showToast(`Votre conversation avec ${conversationName} a été supprimée définitivement`, 'success');
          
        } catch (error) {
          console.error('❌ Erreur suppression:', error);
          showToast('Impossible de supprimer la conversation', 'error');
        } finally {
          setIsDeleting(false);
        }
      },
      onClose: () => setConfirmDialog(null)
    });
  };

  const loadArchivedConversations = async () => {
    try {
      const result = await api.messagerie.getArchivedConversations();
      if (result.success) {
        setArchivedConversations(result.data || []);
      }
    } catch (error) {
      console.error('❌ Erreur chargement archives:', error);
    }
  };

  const searchMessages = async (query) => {
    if (!query.trim()) {
      setSearchResults({ messages: [], conversations: [], users: [] });
      return;
    }

    try {
      setIsSearching(true);
      
      const queryLower = query.toLowerCase().trim();
      
      const matchingConversations = conversations.filter(conv => {
        const name = (conv.display_name || conv.name || '').toLowerCase();
        return name.includes(queryLower);
      });
      
      const matchingUsers = employees.filter(emp => {
        const fullName = `${emp.prenom || ''} ${emp.nom || ''}`.toLowerCase();
        const firstName = (emp.prenom || '').toLowerCase();
        const lastName = (emp.nom || '').toLowerCase();
        const email = (emp.email || '').toLowerCase();
        
        return fullName.includes(queryLower) || 
               firstName.includes(queryLower) || 
               lastName.includes(queryLower) || 
               email.includes(queryLower);
      });
      
      let apiResults = { messages: [], conversations: [], users: [] };
      
      try {
        const [messagesRes, conversationsRes, usersRes] = await Promise.all([
          api.messagerie.searchMessages(query).catch(e => ({ success: false, data: [] })),
          api.messagerie.searchConversations(query).catch(e => ({ success: false, data: [] })),
          api.messagerie.searchUsers(query).catch(e => ({ success: false, data: [] }))
        ]);
        
        apiResults = {
          messages: messagesRes.success ? (messagesRes.data || []) : [],
          conversations: conversationsRes.success ? (conversationsRes.data || []) : [],
          users: usersRes.success ? (usersRes.data || []) : []
        };
        
      } catch (apiError) {
        console.warn('⚠️ Erreur recherche API, utilisation recherche locale uniquement:', apiError);
      }
      
      const combinedUsers = [...matchingUsers];
      
      apiResults.users.forEach(apiUser => {
        const exists = combinedUsers.some(localUser => localUser.id === apiUser.id);
        if (!exists) {
          combinedUsers.push(apiUser);
        }
      });
      
      const filteredConversations = [
        ...matchingConversations,
        ...apiResults.conversations.filter(conv => {
          if (conv.type === 'group') return false;
          return !matchingConversations.some(local => local.id === conv.id);
        })
      ];
      
      const finalResults = {
        messages: apiResults.messages,
        conversations: filteredConversations,
        users: combinedUsers
      };
      
      setSearchResults(finalResults);
      
    } catch (error) {
      console.error('❌ Erreur recherche:', error);
      setSearchResults({ messages: [], conversations: [], users: [] });
    } finally {
      setIsSearching(false);
    }
  };

  // ====================================
  // FONCTIONS AVATAR
  // ====================================

  const getAvatarUrl = useCallback((employee, useCache = true) => {
    if (!employee?.photo_url) return null;
    
    const cacheKey = `${employee.id}_${employee.photo_url}_${forceRefresh}`;
    
    if (useCache && avatarCache.has(cacheKey)) {
      return avatarCache.get(cacheKey);
    }
    
    let generatedUrl = null;
    
    if (employee.photo_url.includes('/')) {
      const pathParts = employee.photo_url.split('/');
      
      if (pathParts.length >= 3) {
        const employeId = pathParts[pathParts.length - 2];
        const filename = pathParts[pathParts.length - 1];
        
        generatedUrl = `/api/messagerie/avatars/${employeId}/${filename}`;
      }
    }
    
    if (!generatedUrl && employee.id && employee.photo_url) {
      const filename = employee.photo_url.split('/').pop();
      generatedUrl = `/api/messagerie/avatars/${employee.id}/${filename}`;
    }
    
    if (!generatedUrl) {
      const filename = employee.photo_url.split('/').pop();
      generatedUrl = `/api/messagerie/avatars/${filename}`;
    }
    
    if (generatedUrl && useCache) {
      setAvatarCache(prev => new Map(prev.set(cacheKey, generatedUrl)));
    }
    
    return generatedUrl;
  }, [avatarCache, forceRefresh]);

  const handleImageError = useCallback((employee, imgElement) => {
    if (employee?.id) {
      setImageErrors(prev => new Set(prev.add(employee.id)));
    }
    
    if (imgElement) {
      imgElement.style.display = 'none';
      
      const initialsElement = imgElement.nextSibling;
      if (initialsElement) {
        initialsElement.style.display = 'flex';
      }
    }
  }, []);

  const renderAvatar = useCallback((employee, size = 40) => {
    if (!employee) {
      return (
        <div 
          className="bg-gray-400 rounded-full flex items-center justify-center text-white font-medium"
          style={{ width: size, height: size }}
        >
          ??
        </div>
      );
    }
    
    const avatarUrl = getAvatarUrl(employee);
    const hasError = imageErrors.has(employee.id);
    const initials = `${employee.prenom?.[0] || ''}${employee.nom?.[0] || ''}`.toUpperCase();
    
    return (
      <div 
        className="bg-[#319190] rounded-full flex items-center justify-center text-white font-medium overflow-hidden relative"
        style={{ width: size, height: size }}
      >
        {avatarUrl && !hasError ? (
          <>
            <img 
              src={`${avatarUrl}?v=${forceRefresh}`}
              alt={`${employee.prenom} ${employee.nom}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                handleImageError(employee, e.target);
              }}
              style={{ display: 'block' }}
              key={`${employee.id}_${forceRefresh}`}
            />
            <span 
              className="w-full h-full flex items-center justify-center absolute inset-0"
              style={{ display: 'none' }}
            >
              {initials || '??'}
            </span>
          </>
        ) : (
          <span className="w-full h-full flex items-center justify-center">
            {initials || '??'}
          </span>
        )}
      </div>
    );
  }, [getAvatarUrl, imageErrors, forceRefresh, handleImageError]);

  // ====================================
  // COMPOSANTS DE LISTE
  // ====================================

  const ConversationList = ({ convs, type, isArchived = false }) => (
    <div className="space-y-2">
      {convs.length === 0 ? (
        <div className="text-center text-gray-500 py-8">
          <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
          <p>
            {isArchived 
              ? 'Aucune conversation archivée'
              : `Aucune conversation ${type === 'individual' ? 'individuelle' : type === 'group' ? 'de groupe' : ''}`
            }
          </p>
        </div>
      ) : (
        convs.map(conversation => (
          <div key={conversation.id} className="group">
            <ConversationItem 
              conversation={conversation} 
              type={type}
              isArchived={isArchived}
            />
          </div>
        ))
      )}
    </div>
  );

  const ConversationItem = ({ conversation, type, isArchived = false }) => {
    return (
      <div className="relative">
        <div
          className={`bg-white rounded-lg p-4 transition-colors cursor-pointer hover:shadow-md border-l-4 ${
            selectedConversation?.id === conversation.id
              ? 'border-[#319190] bg-blue-50'
              : 'border-transparent hover:border-[#319190]'
          }`}
          onClick={() => {
            if (isArchived) return;
            
            if (type === 'group') {
              openGroupConversation(conversation);
            } else {
              openConversation(conversation);
            }
          }}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              {conversation.type === 'individual' ? (
                <div className="relative">
                  {(() => {
                    let employeeData = null;
                    
                    if (conversation.other_participant) {
                      employeeData = conversation.other_participant;
                    }
                    else if (conversation.individual_photo || conversation.photo_url) {
                      employeeData = {
                        id: conversation.other_participant_id,
                        photo_url: conversation.individual_photo || conversation.photo_url,
                        prenom: conversation.display_name?.split(' ')[0] || 'U',
                        nom: conversation.display_name?.split(' ')[1] || 'ser'
                      };
                    }
                    else if (conversation.other_participant_id && employees.length > 0) {
                      employeeData = employees.find(emp => emp.id === conversation.other_participant_id);
                    }
                    
                    if (employeeData) {
                      return renderAvatar(employeeData, 48);
                    }
                    
                    const initials = conversation.display_name?.substring(0, 2).toUpperCase() || '??';
                    
                    return (
                      <div 
                        className="bg-[#319190] rounded-full flex items-center justify-center text-white font-medium"
                        style={{ width: 48, height: 48 }}
                      >
                        {initials}
                      </div>
                    );
                  })()}
                </div>
              ) : (
                <div className="w-12 h-12 bg-[#319190] rounded-full flex items-center justify-center text-white">
                  <Users size={20} />
                </div>
              )}
              
              {/* Indicateur en ligne pour conversations individuelles */}
              {conversation.type === 'individual' && !isArchived && isUserOnline(conversation.other_participant_id) && (
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
              )}
            </div>
            
            <div className="flex-1 min-w-0">
              {/* 🎯 LIGNE 1 : Nom + Heure + Bouton (ALIGNEMENT PARFAIT) */}
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-medium text-gray-900 truncate flex items-center gap-2">
                  {conversation.display_name || conversation.name}
                  {isArchived && (
                    <Archive size={14} className="text-gray-400" />
                  )}
                </h3>
                
                {/* 🎯 CONTENEUR ALIGNÉ : Heure + Badge + Bouton */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Heure */}
                  <span className="text-sm text-gray-500">
                    {isArchived 
                      ? new Date(conversation.archived_at).toLocaleDateString('fr-FR')
                      : (conversation.last_message_at ? formatTimeAgo(conversation.last_message_at) : 'Nouveau')
                    }
                  </span>
                  
                  {/* Badge non lus */}
                  {!isArchived && (conversation.unread_count || 0) > 0 && (
                    <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                      {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                    </div>
                  )}
                  
                  {/* BOUTON ARCHIVE - SEULEMENT pour conversations individuelles */}
                  {conversation.type === 'individual' && (
                    <>
                      {!isArchived ? (
                        // Bouton Archive
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveConversation(conversation.id);
                          }}
                          disabled={isArchiving}
                          className="w-7 h-7 flex items-center justify-center hover:bg-orange-100 rounded-full transition-colors text-gray-400 hover:text-orange-600"
                          title="Archiver"
                        >
                          <Archive size={14} />
                        </button>
                      ) : (
                        // Boutons pour conversations archivées
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              unarchiveConversation(conversation.id);
                            }}
                            disabled={isArchiving}
                            className="w-7 h-7 flex items-center justify-center hover:bg-green-100 rounded-full transition-colors text-gray-400 hover:text-green-600"
                            title="Restaurer"
                          >
                            <ArchiveRestore size={14} />
                          </button>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteConversation(conversation.id);
                            }}
                            disabled={isDeleting}
                            className="w-7 h-7 flex items-center justify-center hover:bg-red-100 rounded-full transition-colors text-gray-400 hover:text-red-600"
                            title="Supprimer"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  
                  {/* Badge non lus pour les groupes (sans bouton) */}
                  {conversation.type === 'group' && !isArchived && (conversation.unread_count || 0) > 0 && (
                    <div className="bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
                      {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                    </div>
                  )}
                </div>
              </div>
              
              {/* 🎯 LIGNE 2 : Message preview */}
              <p className="text-sm text-gray-600 truncate">
                {isArchived 
                  ? 'Conversation archivée' 
                  : (conversation.last_message_content || 'Aucun message')
                }
              </p>
              
              {/* 🎯 LIGNE 3 : Infos supplémentaires */}
              {type === 'group' && !isArchived ? (
                <p className="text-xs text-blue-600 mt-1">
                  🔒 Messages privés entre membres
                </p>
              ) : conversation.type === 'group' && !isArchived && (
                <p className="text-xs text-gray-500 mt-1">
                  {conversation.participant_count} membres
                </p>
              )}
              
              {isArchived && (
                <p className="text-xs text-gray-500 mt-1">
                  Archivée le {new Date(conversation.archived_at).toLocaleDateString('fr-FR')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const SearchResults = () => (
    <div className="space-y-4">
      {/* Résultats utilisateurs */}
      {searchResults.users.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Utilisateurs</h3>
          <div className="space-y-1">
            {searchResults.users.map(user => (
              <div
                key={user.id}
                onClick={async () => {
                  try {
                    console.log(`👤 Clic sur utilisateur: ${user.prenom} ${user.nom} (ID: ${user.id})`);
                    
                    const existingConv = conversations.find(conv => 
                      conv.type === 'individual' && 
                      conv.other_participant_id === user.id
                    );
                    
                    if (existingConv) {
                      console.log('✅ Conversation existante trouvée');
                      openConversation(existingConv);
                      return;
                    }
                    
                    console.log('🆕 Création nouvelle conversation...');
                    
                    const convResult = await api.messagerie.createIndividualConversation(user.id);
                    
                    if (!convResult.success) {
                      throw new Error(convResult.error || 'Erreur création conversation');
                    }
                    
                    console.log('✅ Nouvelle conversation créée:', convResult.data);
                    
                    const newConversation = {
                      ...convResult.data,
                      other_participant: user,
                      other_participant_id: user.id,
                      display_name: `${user.prenom} ${user.nom}`,
                      type: 'individual'
                    };
                    
                    setConversations(prev => [newConversation, ...prev]);
                    
                    await openConversation(newConversation);
                    
                  } catch (error) {
                    console.error('❌ Erreur clic utilisateur:', error);
                    setError(`Impossible d'ouvrir la conversation avec ${user.prenom} ${user.nom}`);
                  }
                }}
                
                className="bg-white rounded-lg p-3 cursor-pointer hover:shadow-md border-l-4 border-transparent hover:border-[#319190] transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#319190] rounded-full flex items-center justify-center text-white font-medium overflow-hidden">
                    {user.photo_url ? (
                      <img src={user.photo_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      `${user.prenom?.charAt(0)}${user.nom?.charAt(0)}`.toUpperCase()
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{user.prenom} {user.nom}</p>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    {user.existing_conversation_id && (
                      <p className="text-xs text-[#319190]">Conversation existante</p>
                    )}
                  </div>
                  {isUserOnline(user.id) && (
                    <Circle size={8} className="text-green-500 fill-current" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Résultats conversations */}
      {searchResults.conversations.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Conversations</h3>
          <ConversationList convs={searchResults.conversations} type="mixed" />
        </div>
      )}

      {/* Résultats messages */}
      {searchResults.messages.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-2">Messages</h3>
          <div className="space-y-2">
            {searchResults.messages.map(message => (
              <div
                key={message.id}
                onClick={() => {
                  const conv = [...conversations, ...groups].find(c => c.id === message.conversation_id);
                  if (conv) {
                    openConversation(conv);
                    setSearchQuery('');
                  }
                }}
                className="bg-white rounded-lg p-3 cursor-pointer hover:shadow-md border-l-4 border-transparent hover:border-[#319190] transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium">{message.display_name}</p>
                  <span className="text-xs text-gray-500">
                    {formatTime(message.created_at)}
                  </span>
                </div>
                <p className="text-sm text-gray-600">{message.content}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Par {message.sender_prenom} {message.sender_nom}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aucun résultat */}
      {searchQuery && 
       searchResults.users.length === 0 && 
       searchResults.conversations.length === 0 && 
       searchResults.messages.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          <Search size={48} className="mx-auto mb-4 opacity-50" />
          <p>Aucun résultat pour "{searchQuery}"</p>
        </div>
      )}
    </div>
  );

  // ====================================
  // FONCTIONS UTILITAIRES DE FORMATAGE
  // ====================================

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('Erreur formatTime:', error);
      return '';
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const now = new Date();
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const diffMs = now - date;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      
      const isToday = now.toDateString() === date.toDateString();
      
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = yesterday.toDateString() === date.toDateString();
      
      if (diffMinutes < 1) {
        return "À l'instant";
      }
      
      if (isToday) {
        return date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      
      if (isYesterday) {
        return `Hier ${date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }
      
      if (diffMs < 7 * 24 * 60 * 60 * 1000) {
        const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
        const dayName = dayNames[date.getDay()];
        return `${dayName} ${date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }
      
      if (now.getFullYear() === date.getFullYear()) {
        return `${date.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit'
        })} ${date.toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }
      
      return `${date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })} ${date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      })}`;
      
    } catch (error) {
      console.warn('Erreur formatTimeAgo:', error);
      return '';
    }
  };

  // ====================================
  // 🆕 INDICATEUR D'ÉTAT HYBRIDE
  // ====================================

  const renderConnectionStatus = () => (
    <div className="flex items-center gap-2 text-sm">
      <div className="flex items-center gap-1">
        {isConnected ? (
          <>
            <Circle size={8} className="text-green-500 fill-current" />
            <span className="text-gray-600">Temps réel</span>
          </>
        ) : isReconnecting ? (
          <>
            <Circle size={8} className="text-orange-500 fill-current" />
            <span className="text-gray-600">Reconnexion...</span>
          </>
        ) : (
          <>
            <Circle size={8} className="text-blue-500 fill-current" />
            <span className="text-gray-600">Mode sécurisé</span>
          </>
        )}
      </div>
      
      {syncInProgress && (
        <div className="flex items-center gap-1">
          <Loader2 size={12} className="animate-spin text-blue-500" />
          <span className="text-xs text-gray-500">Sync...</span>
        </div>
      )}
      
      {lastSyncTimestamp && (
        <span className="text-xs text-gray-400">
          Sync: {new Date(lastSyncTimestamp).toLocaleTimeString()}
        </span>
      )}
    </div>
  );

  // ====================================
  // EFFECTS
  // ====================================

  useEffect(() => {
  if (isOpen) {
    document.body.style.overflow = 'hidden';
    
    // ✅ UTILISER setTimeout pour éviter la cascade immédiate
    const loadDataTimeout = setTimeout(() => {
      fetchDashboard();
      fetchEmployees('');
      loadArchivedConversations();
    }, 100); // ← DÉLAI pour éviter la cascade de re-renders
    
    return () => {
      clearTimeout(loadDataTimeout);
      document.body.style.overflow = 'auto';
    };
  }
  
  return () => {
    document.body.style.overflow = 'auto';
  };
}, [isOpen]);


  useEffect(() => {
  if (searchQuery.trim()) {
    const timeoutId = setTimeout(() => {
      searchMessages(searchQuery);
    }, 300);
    return () => clearTimeout(timeoutId);
  } 
}, [searchQuery]); 



const stableScrollToBottom = useCallback(() => {
  scrollToBottom();
}, [])


  useEffect(() => {
  stableScrollToBottom();
}, [messages]);


  // 🆕 GESTION TYPING AVEC LE HOOK
  useEffect(() => {
  // ✅ VÉRIFICATION PRÉALABLE - évite les effets inutiles
  if (!selectedConversation?.id || !isConnected) return;
  
  if (chatMessageContent.trim()) {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    if (socketRef.current) {
      socketRef.current.emit('typing_start', { 
        conversationId: selectedConversation.id 
      });
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      if (socketRef.current) {
        socketRef.current.emit('typing_stop', { 
          conversationId: selectedConversation.id 
        });
      }
    }, 1000);
  } else {
    if (socketRef.current) {
      socketRef.current.emit('typing_stop', { 
        conversationId: selectedConversation.id 
      });
    }
  }
  
  return () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };
}, [chatMessageContent, selectedConversation?.id, isConnected]);



  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setAvatarCache(prev => {
        if (prev.size > 50) {
          const newCache = new Map();
          const entries = Array.from(prev.entries()).slice(-25);
          entries.forEach(([key, value]) => newCache.set(key, value));
          console.log('🧹 [Avatar Cache] Nettoyage effectué');
          return newCache;
        }
        return prev;
      });
    }, 5 * 60 * 1000);
    
    return () => clearInterval(cleanupInterval);
  }, []);

  // ====================================
  // RENDER PRINCIPAL
  // ====================================

  if (!isOpen) return null;

  return (
    <AnimatePresence mode="wait">
      <div className="fixed inset-0 flex items-start justify-start isolate" style={{ zIndex: 1000 }}>
        <motion.div 
          className="fixed inset-0 bg-black/30"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />
        
        <motion.div
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          exit={{ x: '-100%' }}
          className="relative w-full max-w-4xl h-screen bg-gray-50/95 backdrop-blur-sm shadow-xl flex flex-col"
        >
          {/* Header avec nouveaux indicateurs */}
          <div className="flex justify-between items-center px-4 py-2 border-b bg-white/90">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold text-[#319190]">Messagerie</h2>
              
              {/* 🆕 NOUVEAUX INDICATEURS HYBRIDES */}
              {renderConnectionStatus()}
              
              {/* 🆕 BOUTON SYNC MANUEL */}
              <button
                onClick={forceSync}
                disabled={syncInProgress}
                className="px-2 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-50"
                title="Synchroniser manuellement"
              >
                {syncInProgress ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  '🔄'
                )}
              </button>
            </div>
            
            <button 
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="text-gray-500 hover:text-gray-700" size={20} />
            </button>
          </div>

          {/* Affichage des erreurs */}
          {error && (
            <div className="bg-red-50 border border-red-200 p-3 mx-4 mt-2 rounded-lg flex items-center gap-2">
              <AlertCircle size={16} className="text-red-500" />
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                <X size={16} />
              </button>
            </div>
          )}

          {selectedConversation ? (
            <ConversationView
              selectedConversation={selectedConversation}
              setSelectedConversation={setSelectedConversation}
              leaveConversation={leaveConversation}
              isUserOnline={isUserOnline}
              isConnected={isConnected}
              loading={loading}
              messages={messages}
              chatMessageContent={chatMessageContent}
              setChatMessageContent={setChatMessageContent}
              attachments={attachments}
              removeAttachment={removeAttachment}
              fileInputRef={fileInputRef}
              handleFileAttachment={handleFileAttachment}
              sending={sending}
              sendMessage={sendMessage}
              typingUsers={typingUsers}
              messagesEndRef={messagesEndRef}
              renderAvatar={renderAvatar}
              messageReadStatus={messageReadStatus}
              markMessageAsReadSocket={markMessageAsReadSocket}
            />
          ) : (
            <>
              {/* Navigation */}
              <div className="flex gap-2 p-3 bg-white/80 border-b">
                <button
                  onClick={() => setCurrentView('new')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                    ${currentView === 'new' ? 'bg-[#319190] text-white' : 'hover:bg-gray-100'}`}
                >
                  <Plus size={16} />
                  Nouveau message
                </button>
                
                <button
                  onClick={() => setCurrentView('conversations')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                    ${currentView === 'conversations' ? 'bg-[#319190] text-white' : 'hover:bg-gray-100'}`}
                >
                  <MessageCircle size={16} />
                  Mes conversations
                  {conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0) > 0 && (
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                      {conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0)}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => setCurrentView('groups')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                    ${currentView === 'groups' ? 'bg-[#319190] text-white' : 'hover:bg-gray-100'}`}
                >
                  <Users size={16} />
                  Groupes de diffusion
                  {groups.reduce((total, group) => total + (group.unread_count || 0), 0) > 0 && (
                    <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-500 text-white">
                      {groups.reduce((total, group) => total + (group.unread_count || 0), 0)}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => setCurrentView('archived')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors
                    ${currentView === 'archived' ? 'bg-[#319190] text-white' : 'hover:bg-gray-100'}`}
                >
                  <Archive size={16} />
                  Archives
                </button>
              </div>

              {/* Search - Seulement pour conversations*/}
              {currentView === 'conversations' && !selectedConversation && (
                <div className="p-3 border-b">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Rechercher une conversation, un message ou un utilisateur..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-[#319190] focus:border-[#319190]"
                    />
                    <Search className="absolute left-3 top-2.5 text-gray-400" size={20} />
                    {isSearching && (
                      <Loader2 className="absolute right-3 top-2.5 text-gray-400 animate-spin" size={20} />
                    )}
                  </div>
                </div>
              )}

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-3">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <Loader2 size={24} className="animate-spin text-[#319190]" />
                  </div>
                ) : searchQuery ? (
                  <SearchResults />

                ) : currentView === 'new' ? (
                  <NewMessageForm
                    selectedRecipients={selectedRecipients}
                    setSelectedRecipients={setSelectedRecipients}
                    messageSubject={messageSubject}
                    setMessageSubject={setMessageSubject}
                    newMessageContent={newMessageContent}
                    setNewMessageContent={setNewMessageContent}
                    attachments={attachments}
                    handleFileAttachment={handleFileAttachment}
                    removeAttachment={removeAttachment}
                    sending={sending}
                    sendResults={sendResults}
                    resetForm={resetForm}
                    handleSendUnified={handleSendUnified}
                    error={error}
                  />
                ) : currentView === 'conversations' ? (
                  <ConversationList convs={conversations} type="individual" />
                ) : currentView === 'groups' ? (
                  <ConversationList convs={groups} type="group" />
                ) : currentView === 'archived' ? (
                  <div className="space-y-4">
                    {/* Header Archives */}
                    <div className="bg-white rounded-lg p-4 border-l-4 border-orange-400">
                      <div className="flex items-center gap-3">
                        <Archive size={24} className="text-orange-600" />
                        <div>
                          <h3 className="font-medium text-gray-900">Conversations archivées</h3>
                          <p className="text-sm text-gray-600">
                            {archivedConversations.length} conversation{archivedConversations.length !== 1 ? 's' : ''} archivée{archivedConversations.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Liste des conversations archivées */}
                    {archivedConversations.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <Archive size={48} className="mx-auto mb-4 opacity-50" />
                        <p>Aucune conversation archivée</p>
                        <p className="text-sm text-gray-400 mt-2">
                          Les conversations archivées apparaîtront ici
                        </p>
                      </div>
                    ) : (
                      <ConversationList 
                        convs={archivedConversations} 
                        type="individual" 
                        isArchived={true}
                      />
                    )}

                    {/* Actions en cours */}
                    {(isArchiving || isDeleting) && (
                      <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg flex items-center gap-2">
                        <Loader2 size={16} className="animate-spin text-blue-600" />
                        <p className="text-sm text-blue-700">
                          {isArchiving ? 'Traitement de l\'archivage...' : 'Suppression en cours...'}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center text-gray-500 mt-8">
                    <p>Vue non reconnue</p>
                  </div>
                )}
              </div>
            </>
          )}
        </motion.div>
      </div>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={true}
          title={confirmDialog.title}
          description={confirmDialog.description}  
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
          type={confirmDialog.type}
         onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onClose}          
        />
      )}

      {/* Toasts */}
      <div className="fixed top-4 right-4 space-y-2" style={{ zIndex: 1001 }}>
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => closeToast(toast.id)}
          />
        ))}
      </div>
    </AnimatePresence>
  );
};

export default MessageForm;